import React, { useMemo, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormContext } from "react-hook-form";

import { HookFormInputGroup, HookFormRadioButton } from "components";
import { useBackendPlatform } from "hooks";
import { TerminalFileBuildOptionTypes, TerminalGatewayTypes } from "types";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";
import {
	maxLength50,
	normalizeAll,
	onlyAlphanumericAndSpecialCharacters,
	required,
} from "validators/validation";

import styles from "../softwareOptions.module.scss";

type FileBuildInformationProps = {
	sectionName?: string;
	isLoading?: boolean;
	backEndPlatformType: string;
	isTerminal?: boolean;
};

const FileBuildInformation = ({
	sectionName = "fileBuild",
	isLoading = false,
	backEndPlatformType,
	isTerminal = false,
}: FileBuildInformationProps) => {
	const { register, watch, clearErrors, setValue } = useFormContext();
	const watchFileBuildInformation = watch(`${sectionName}.fileBuildOption`);
	const gatewayName = watch(`gateway.gatewayName`);

	const isGatewayArise = gatewayName === TerminalGatewayTypes.ARISE.name;

	const { isBackEndPlatformTsys, isBackEndPlatformStraddle } =
		useBackendPlatform(backEndPlatformType);

	useEffect(() => {
		if (
			watchFileBuildInformation !==
			TerminalFileBuildOptionTypes.Other.name
		) {
			clearErrors(`${sectionName}.fileType`);
		}
	}, [clearErrors, sectionName, watchFileBuildInformation]);

	useEffect(() => {
		if (isBackEndPlatformStraddle) {
			setValue(
				`${sectionName}.fileBuildOption`,
				TerminalFileBuildOptionTypes.Other.name,
			);
		}
	}, [isBackEndPlatformStraddle, sectionName, setValue]);

	const validateFileType = useMemo(
		() =>
			watchFileBuildInformation ===
			TerminalFileBuildOptionTypes.Other.name
				? { required, maxLength50 }
				: undefined,
		[watchFileBuildInformation],
	);

	return (
		<>
			<p className={styles.grayTitle}>File Build Information</p>
			<Row className={styles.columnDirection}>
				{isBackEndPlatformTsys && (
					<>
						<Col
							md={2}
							data-autotest="tsysStageOnlyVarFileBuildColumn"
						>
							<HookFormRadioButton
								id={`${sectionName}.TsysStageOnlyVar`}
								register={register}
								name={`${sectionName}.fileBuildOption`}
								value={
									TerminalFileBuildOptionTypes
										.TsysStageOnlyVar.name
								}
								label="TSYS Stage Only Var"
								blurLoading={isLoading}
								disabled={isGatewayArise}
							/>
						</Col>
						<Col md={2} data-autotest="tsysSummitFileBuildColumn">
							<HookFormRadioButton
								id={`${sectionName}.TsysSummit`}
								register={register}
								name={`${sectionName}.fileBuildOption`}
								value={
									TerminalFileBuildOptionTypes.TsysSummit.name
								}
								label="TSYS Summit"
								blurLoading={isLoading}
								disabled={isGatewayArise}
							/>
						</Col>
						{!isTerminal && (
							<Col
								md={2}
								data-autotest="tsysTransitFileBuildColumn"
							>
								<HookFormRadioButton
									id={`${sectionName}.TsysTransit`}
									register={register}
									name={`${sectionName}.fileBuildOption`}
									value={
										TerminalFileBuildOptionTypes.TsysTransit
											.name
									}
									label="TSYS Transit"
									blurLoading={isLoading}
								/>
							</Col>
						)}
					</>
				)}
				<Col md={2} data-autotest="otherFileBuildColumn">
					<HookFormRadioButton
						id={`${sectionName}.Other`}
						register={register}
						name={`${sectionName}.fileBuildOption`}
						value={TerminalFileBuildOptionTypes.Other.name}
						label="Other"
						blurLoading={isLoading}
						disabled={isGatewayArise}
					/>
				</Col>
			</Row>
			<Row>
				<Col md={5} data-autotest="fileTypeColumn">
					<HookFormInputGroup
						className={styles.fileTypeMoveLeft}
						name={`${sectionName}.fileType`}
						register={register}
						label="File Type"
						colInput={6}
						colLabel={6}
						disabled={
							watchFileBuildInformation !==
							TerminalFileBuildOptionTypes.Other.name
						}
						showRequiredMark={
							watchFileBuildInformation ===
							TerminalFileBuildOptionTypes.Other.name
						}
						validator={validateFileType}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						blurLoading={isLoading}
						autoComplete="off"
						normalizeOnBlur={trimNormalizer}
					/>
				</Col>
			</Row>
		</>
	);
};

export default FileBuildInformation;
